'use client'
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect
} from 'react'
import { getFromStorage, saveToStorage } from '../helpers/storage.helper'
import { getInitialSetting } from '../helpers/getInitialSetting'
import { frbSetUserProperties } from '@/helpers/analytics.helper'
import moment from 'moment'

const LanguageContext = createContext()

const LANGS = {
  en: { label: 'English', dir: 'ltr' },
  ar: { label: 'العربية', dir: 'rtl' }
}

export function useLanguage () {
  return useContext(LanguageContext)
}

export default function LanguageProvider ({ children }) {
  const [lang, setLangState] = useState(() => {
    const allowed = ['en', 'ar', 'english', 'arabic']
    const val = getInitialSetting({
      key: 'lang',
      urlParam: 'language',
      allowed,
      fallback: () => {
        if (typeof navigator !== 'undefined') {
          const navLang = navigator.language?.toLowerCase() || ''
          if (navLang.startsWith('ar')) return 'ar'
          if (navLang.startsWith('en')) return 'en'
        }
        return 'en'
      }
    })
    if (val === 'arabic' || val === 'ar') return 'ar'
    if (val === 'english' || val === 'en') return 'en'
    return 'en'
  })
  const dir = LANGS[lang]?.dir || 'ltr'

  useEffect(() => {
    const allowed = ['en', 'ar', 'english', 'arabic']
    const val = getInitialSetting({
      key: 'lang',
      urlParam: 'language',
      allowed,
      fallback: () => {
        if (typeof navigator !== 'undefined') {
          const navLang = navigator.language?.toLowerCase() || ''
          if (navLang.startsWith('ar')) return 'ar'
          if (navLang.startsWith('en')) return 'en'
        }
        return 'en'
      }
    })
    if (val === 'arabic' || val === 'ar') setLangState('ar')
    else if (val === 'english' || val === 'en') setLangState('en')
    else setLangState('en')
  }, [])

  useEffect(() => {
    if (lang === 'ar' || lang === 'en') {
      saveToStorage('lang', lang)
    }
    frbSetUserProperties({
      language: lang ?? null
    })
  }, [lang])

  useEffect(() => {
    const months = [
      lang == 'ar' ? 'يناير' : 'January',
      lang == 'ar' ? 'فبراير' : 'February',
      lang == 'ar' ? 'مارس' : 'March',
      lang == 'ar' ? 'أبريل' : 'April',
      lang == 'ar' ? 'مايو' : 'May',
      lang == 'ar' ? 'يونيو' : 'June',
      lang == 'ar' ? 'يوليو' : 'July',
      lang == 'ar' ? 'أغسطس' : 'August',
      lang == 'ar' ? 'سبتمبر' : 'September',
      lang == 'ar' ? 'أكتوبر' : 'October',
      lang == 'ar' ? 'نوفمبر' : 'November',
      lang == 'ar' ? 'ديسمبر' : 'December'
    ]
    const shortMonths = [
      lang == 'ar' ? 'يناير' : 'Jan',
      lang == 'ar' ? 'فبراير' : 'Feb',
      lang == 'ar' ? 'مارس' : 'Mar',
      lang == 'ar' ? 'أبريل' : 'Apr',
      lang == 'ar' ? 'مايو' : 'May',
      lang == 'ar' ? 'يونيو' : 'Jun',
      lang == 'ar' ? 'يوليو' : 'Jul',
      lang == 'ar' ? 'أغسطس' : 'Aug',
      lang == 'ar' ? 'سبتمبر' : 'Sept',
      lang == 'ar' ? 'أكتوبر' : 'Oct',
      lang == 'ar' ? 'نوفمبر' : 'Nov',
      lang == 'ar' ? 'ديسمبر' : 'Dec'
    ]

    const weekdaysShort =
      lang == 'ar'
        ? [
            'الأحد',
            'الاثنين',
            'الثلاثاء',
            'الأربعاء',
            'الخميس',
            'الجمعة',
            'السبت'
          ]
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    moment.updateLocale('en', {
      weekdaysShort,
      months: months,
      monthsShort: shortMonths,
      relativeTime: {
        future: lang == 'ar' ? 'بعد %s' : 'in %s',
        past: lang == 'ar' ? 'قبل %s' : '%s ago',
        s: lang == 'ar' ? 'بضع ثوان' : 'a few seconds',
        ss: lang == 'ar' ? '%d ثوان' : '%d seconds',
        m: lang == 'ar' ? 'دقيقة' : 'a minute',
        mm: lang == 'ar' ? '%d دقائق' : '%d minutes',
        h: lang == 'ar' ? 'ساعة' : 'an hour',
        hh: lang == 'ar' ? '%d ساعات' : '%d hours',
        d: lang == 'ar' ? 'يوم' : 'a day',
        dd: lang == 'ar' ? '%d أيام' : '%d days',
        w: lang == 'ar' ? 'أسبوع' : 'a week',
        ww: lang == 'ar' ? '%d أسابيع' : '%d weeks',
        M: lang == 'ar' ? 'شهر' : 'a month',
        MM: lang == 'ar' ? '%d أشهر' : '%d months',
        y: lang == 'ar' ? 'عام' : 'a year',
        yy: lang == 'ar' ? '%d أعوام' : '%d years'
      }
    })
  }, [lang])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dir = dir
      document.documentElement.lang = lang
    }
  }, [dir, lang])

  const setLang = newLang => {
    setLangState(newLang)
  }

  const value = useMemo(
    () => ({
      lang,
      setLang,
      dir
    }),
    [lang, dir]
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
